import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import Input from "components/common/form/inputs/Input";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import FormActions from "components/common/form/partials/FormActions";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { DropdownInputPropsType } from "types/form-types";
import { BookingAdditionalChargesCalculationTypes, BookingAdditionalChargesTemplateInput, GetBookingAdditionalChargesTemplateFormDataQuery, GetBookingAdditionalChargesTemplateFormDataQueryVariables, SaveBookingAdditionalChargesTemplateMutation, SaveBookingAdditionalChargesTemplateMutationVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";

type Model = {
    name: string | null
    calculationType: BookingAdditionalChargesCalculationTypes | null
    amount: number | string | null
    id: string | number | null,
    isIncome: boolean
}

type BookingAdditionalChargesTemplateFormPropsType = {
    model?: string | number | BookingAdditionalChargesTemplateInput
    name: string,
    onSave?: (id: number) => void
}

const query = gql`
query GetBookingAdditionalChargesTemplateFormData($id: Int) {
    bookingAdditionalChargesTemplate(id: $id) {
      amount
      calculationType
      name
      id
      isIncome
    }
  }
`;

const mutation = gql`
mutation SaveBookingAdditionalChargesTemplate($model: BookingAdditionalChargesTemplateInput!) {
    bookingAdditionalChargesTemplate(model: $model) {
      id
    }
  }
`;

const BookingAdditionalChargesTemplateForm = (props: BookingAdditionalChargesTemplateFormPropsType) => {
    const context = useAppContext();
    const [model, setModel] = useState<Model>({
        name: '',
        amount: '',
        calculationType: null,
        id: null,
        isIncome: false
    });
    const [disableButtons, setDisableButtons] = useState<boolean>(false);
    const [componentState, setComponentState] = useState<ComponentStateTypes>('');

    const calculationTypeConfig: DropdownInputPropsType<BookingAdditionalChargesCalculationTypes | null> = {
        name: 'calculationType',
        label: 'fields.calculationType.name',
        required: true,
        dataSource: {
            data: [
                BookingAdditionalChargesCalculationTypes.Fix,
                BookingAdditionalChargesCalculationTypes.PerNight,
                BookingAdditionalChargesCalculationTypes.PerNightPerPerson,
                BookingAdditionalChargesCalculationTypes.PerPerson,
                BookingAdditionalChargesCalculationTypes.Percentage
            ]
        },
        getItemId: item => item!,
        view: {
            inputView: item => item ? context.t(`accommodation.additionalChargesTemplate.calculationType.${item?.toLocaleLowerCase()}`) : ''
        }
    }

    useEffect(() => {
        loadModel().then();
    }, [props.model]);

    const loadModel = async () => {
        if(!Number(props.model)) return;
        setComponentState('loading');
        try {
            const response = await graph<GetBookingAdditionalChargesTemplateFormDataQuery, GetBookingAdditionalChargesTemplateFormDataQueryVariables>({
                query,
                variables: {
                    id: Number(props.model) || Number((props.model as any).id)
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            setModel(response.data!.bookingAdditionalChargesTemplate!);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    const onChange = (name: keyof BookingAdditionalChargesTemplateInput, value: any) => {
        setModel({
            ...model,
            [name]: value
        });
    }

    const onSubmit = async () => {
        setDisableButtons(true);
        try {
            const response = await graph<SaveBookingAdditionalChargesTemplateMutation, SaveBookingAdditionalChargesTemplateMutationVariables>({
                query: mutation,
                variables: {
                    model: {
                        amount: Number(model.amount),
                        calculationType: model.calculationType!,
                        name: model.name!,
                        id: Number(model.id) || 0,
                        isIncome: model.isIncome
                    }
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            context.notify('success', 'request.save.success');
            if(props.onSave) {
                props.onSave(response.data!.bookingAdditionalChargesTemplate?.id!);
            } else {
                context.navigate(-1, undefined, {
                    replace: true
                })
            }
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        }
    }

    return (
        <ComponentState state={componentState}>
            <Form name={`${props.name}-booking-additional-charges-template-form`} onSubmit={onSubmit}>
                <Input
                    name="name"
                    label="fields.name.name"
                    required
                    maxLength={128}
                    value={model?.name ?? ''}
                    onChange={v => onChange('name', v)}
                />
                <DropdownInput
                    {...calculationTypeConfig}
                    value={model?.calculationType}
                    onChange={v => onChange('calculationType', v)}
                />
                <Input
                    name="amount"
                    label="fields.amount.name"
                    required
                    type="number"
                    value={model?.amount?.toString() ?? ''}
                    onChange={v => onChange('amount', v)}
                />
                <CheckboxInput
                    name="isIncome"
                    label="fields.isIncome.name"
                    value={model?.isIncome ?? false}
                    onChange={v => onChange('isIncome', v)}
                />
                <FormActions disableInputs={disableButtons} />
            </Form>
        </ComponentState>
    );
};

export default BookingAdditionalChargesTemplateForm;
